import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    IconButton,
    InputAdornment
} from "@mui/material";
import AppButton from "../../components/common/AppButton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { updateInternalPassword, reset } from "../../features/auth/authSlice";
import AppSnackbar from "../../components/common/AppSnackbar";
import { styles } from '../../components/changePassword/styles';
import { UpdatePasswordDTO } from "../../dtos/auth";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoadingSpinner from "../../components/common/Loading";

export function ChangePasswordComponent({ loginType }: any) {
    const dispatch = useAppDispatch();
    const [disabledCondition, setDisabledCondition] = useState(true);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showVerifyPassword, setShowVerifyPassword] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    const handleToggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };
    const handleToggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };
    const handleToggleVerifyPasswordVisibility = () => {
        setShowVerifyPassword(!showVerifyPassword);
    };
    useEffect(() => {
        dispatch(reset())
    }, [dispatch])
    const [showAlert, setShowAlert] = useState(false);  // State for showing alert
    const pharmacyID = Number(localStorage?.getItem("pharmacyId"));
    const auth = useAppSelector((state: { auth: any; }) => state.auth);
    const updatePassword = useCallback((payload: UpdatePasswordDTO) => {
        dispatch(updateInternalPassword(payload));
    }, [dispatch]);

    useEffect(() => {
        if (auth?.isUpdatePasswordSuccess || auth?.isUpdatePasswordError) {
            setShowAlert(true);
            if (auth?.isUpdatePasswordSuccess) {
                setShowErrors(false)
                setConfirmPassword("");
                setCurrentPassword("");
                setNewPassword("");
            }
            else if (auth?.isUpdatePasswordError) {
                setShowErrors(true)
            }
            setTimeout(() => {
                dispatch(reset());
                setShowAlert(false);  // Hide the alert after 4 seconds
            }, 4500);
        }
    }, [auth?.isUpdatePasswordSuccess, auth?.isUpdatePasswordError, dispatch]); // Trigger alert on state change

    useEffect(() => {
        if (currentPassword?.trim() !== "" && newPassword?.trim() !== "" && confirmPassword?.trim() !== "") {
            if (newPassword === confirmPassword) {
                setDisabledCondition(false);
            } else {
                setDisabledCondition(true);
            }
        } else {
            setDisabledCondition(true);
        }
    }, [currentPassword, newPassword, confirmPassword])

    const handleSubmit = () => {
        const payload = {
            pharmacy_id: pharmacyID,
            current_password: currentPassword,
            new_password: newPassword,
            account_type: loginType
        };
        if (!disabledCondition) {
            updatePassword(payload);
        }
        else {
            setShowErrors(true)
        }
    };

    return (
        <Container component="main">
            {auth?.isLoading && <LoadingSpinner fullScreen={true} />}
            {<AppSnackbar
                message={auth?.isUpdatePasswordSuccess ? "Updated Successfully" : auth?.isUpdatePasswordError && auth?.updatePasswordMessage}
                open={showAlert && (auth?.isUpdatePasswordSuccess || auth?.isUpdatePasswordError)}
                handleClose={() => setShowAlert(false)}
                severity={auth?.isUpdatePasswordSuccess ? "success" : auth?.isUpdatePasswordError ? "error" : "success"}
            />}
            <Box sx={styles.container}>
                <Typography sx={styles.heading}>
                    {`Change Password for ${loginType === 0 ? "Admin" : loginType === 1 && "Staff"}`}
                </Typography>
                <Typography sx={styles.subHeading}>
                    Change Your Account Password
                </Typography>
                {/* Current Password */}
                <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>
                        Current Password
                    </Typography>
                    <Box sx={styles.currentPasswordBox}>
                        <TextField
                            id="current-password"
                            variant="outlined"
                            placeholder="Enter Current Password"
                            type={showCurrentPassword ? "text" : "password"}
                            value={currentPassword}
                            sx={styles.textField}
                            onChange={(event) => setCurrentPassword(event.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleToggleCurrentPasswordVisibility}
                                            edge="end"
                                        >
                                            {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {showErrors && disabledCondition && currentPassword?.trim() === "" && <Typography
                            sx={styles.error}
                        >
                            *Field Required
                        </Typography>}
                    </Box>

                </Box>
                {/* New Password */}
                <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>
                        New Password
                    </Typography>
                    <Box sx={styles.currentPasswordBox}>
                        <TextField
                            id="new-password"
                            variant="outlined"
                            placeholder="Enter New Password"
                            type={showNewPassword ? "text" : "password"}
                            sx={styles.textField}
                            value={newPassword}
                            onChange={(event) => setNewPassword(event.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleToggleNewPasswordVisibility}
                                            edge="end"
                                        >
                                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {showErrors && disabledCondition && (newPassword?.trim() === "") && <Typography
                            sx={styles.error}
                        >
                            *Field Required
                        </Typography>}
                    </Box>
                </Box>
                {/* Verify Password */}
                <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>
                        Verify Password
                    </Typography>
                    <Box sx={styles.currentPasswordBox}>
                        <TextField
                            id="verify-password"
                            variant="outlined"
                            placeholder="Verify New Password"
                            type={showVerifyPassword ? "text" : "password"}
                            sx={styles.textField}
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleToggleVerifyPasswordVisibility}
                                            edge="end"
                                        >
                                            {showVerifyPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {showErrors && disabledCondition && (confirmPassword?.trim() === "") && <Typography
                            sx={styles.error}
                        >
                            *Field Required
                        </Typography>}
                        {showErrors && disabledCondition && (confirmPassword?.trim() !== "" && newPassword?.trim() !== "") && (newPassword !== confirmPassword) && <Typography
                            sx={styles.error}
                        >
                            *Does not match with new password
                        </Typography>}
                    </Box>
                </Box>
                <Box sx={styles.fieldContainer}>
                    <Box sx={styles.appButtonBox}>
                        <AppButton title="✓ Save" onClick={handleSubmit} sx={styles.appButton} />
                    </Box>
                </Box>
            </Box>
        </Container>
    );
}